/**
 * Settings Tab Component
 * Form-level settings and metadata configuration
 */

import React, { useCallback } from 'react';
import type { FormMetadata, FormSection } from '@atlas-forms/types-js';
import { useUIStore } from '@atlas-forms/state-react';
import '../../styles/tabs/SettingsTab.css';

export interface SettingsTabProps {
  metadata: FormMetadata;
  sections?: FormSection[];
  onUpdateMetadata: (updates: Partial<FormMetadata>) => void;
  onUpdateFormSettings: (updates: { sections?: FormSection[] }) => void;
}

/**
 * Settings Tab - Form-level configuration
 */
export const SettingsTab: React.FC<SettingsTabProps> = ({
  metadata,
  sections = [],
  onUpdateMetadata,
  onUpdateFormSettings,
}) => {
  const { theme } = useUIStore();

  const handleMetadataChange = useCallback(
    (key: keyof FormMetadata, value: unknown) => {
      onUpdateMetadata({ [key]: value });
    },
    [onUpdateMetadata]
  );

  const handleAddSection = useCallback(() => {
    const newSection: FormSection = {
      id: `section-${Date.now()}`,
      title: 'New Section',
      description: '',
      order: sections.length,
      collapsible: false,
      defaultExpanded: true,
    };
    onUpdateFormSettings({ sections: [...sections, newSection] });
  }, [sections, onUpdateFormSettings]);

  const handleDeleteSection = useCallback(
    (id: string) => {
      onUpdateFormSettings({
        sections: sections.filter((s) => s.id !== id),
      });
    },
    [sections, onUpdateFormSettings]
  );

  const handleUpdateSection = useCallback(
    (id: string, updates: Partial<FormSection>) => {
      onUpdateFormSettings({
        sections: sections.map((s) => (s.id === id ? { ...s, ...updates } : s)),
      });
    },
    [sections, onUpdateFormSettings]
  );

  return (
    <div className="af-settings-tab" data-theme={theme}>
      <div className="af-settings-tab__content">
        {/* Form Metadata */}
        <div className="af-settings-tab__section">
          <h3 className="af-settings-tab__section-title">Form Metadata</h3>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Form ID
              <input
                type="text"
                value={metadata.formID}
                disabled
                className="af-settings-tab__input"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Title
              <input
                type="text"
                value={metadata.title || ''}
                onChange={(e) => handleMetadataChange('title', e.target.value)}
                className="af-settings-tab__input"
                placeholder="Form title"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Subtitle
              <input
                type="text"
                value={metadata.subtitle || ''}
                onChange={(e) => handleMetadataChange('subtitle', e.target.value)}
                className="af-settings-tab__input"
                placeholder="Form subtitle"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Description
              <textarea
                value={metadata.description || ''}
                onChange={(e) => handleMetadataChange('description', e.target.value)}
                className="af-settings-tab__textarea"
                placeholder="Form description"
                rows={4}
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Code
              <input
                type="text"
                value={metadata.code || ''}
                onChange={(e) => handleMetadataChange('code', e.target.value)}
                className="af-settings-tab__input"
                placeholder="Form code identifier"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Version
              <input
                type="text"
                value={metadata.version || ''}
                onChange={(e) => handleMetadataChange('version', e.target.value)}
                className="af-settings-tab__input"
                placeholder="e.g., 1.0.0"
              />
            </label>
          </div>
        </div>

        {/* Form Style */}
        <div className="af-settings-tab__section">
          <h3 className="af-settings-tab__section-title">Form Style</h3>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Form Style
              <select
                value={metadata.formStyle || 'default'}
                onChange={(e) => handleMetadataChange('formStyle', e.target.value)}
                className="af-settings-tab__select"
              >
                <option value="default">Default</option>
                <option value="webpage">Webpage</option>
              </select>
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Number of Columns
              <input
                type="number"
                min="1"
                max="12"
                value={metadata.numberOfColumns || 1}
                onChange={(e) => handleMetadataChange('numberOfColumns', parseInt(e.target.value))}
                className="af-settings-tab__input"
              />
            </label>
          </div>

          <div className="af-settings-tab__checkbox-group">
            <label className="af-settings-tab__checkbox-label">
              <input
                type="checkbox"
                checked={metadata.enabled || false}
                onChange={(e) => handleMetadataChange('enabled', e.target.checked)}
              />
              Enabled
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Content Style
              <select
                value={metadata.formContentStyle ?? 'input'}
                onChange={(e) => handleMetadataChange('formContentStyle', e.target.value)}
                className="af-settings-tab__input"
              >
                <option value="input">Input (640px max)</option>
                <option value="web-content">Web Content (960px max)</option>
                <option value="dashboard">Dashboard (full width)</option>
                <option value="data">Data (full width)</option>
              </select>
            </label>
          </div>
        </div>

        {/* Category & Type */}
        <div className="af-settings-tab__section">
          <h3 className="af-settings-tab__section-title">Organization</h3>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Category ID
              <input
                type="number"
                value={metadata.formCategoryId || ''}
                onChange={(e) => handleMetadataChange('formCategoryId', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-settings-tab__input"
                placeholder="Leave blank for default"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Type ID
              <input
                type="number"
                value={metadata.formTypeId || ''}
                onChange={(e) => handleMetadataChange('formTypeId', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-settings-tab__input"
                placeholder="Leave blank for default"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Display Order
              <input
                type="number"
                value={metadata.displayOrder || 0}
                onChange={(e) => handleMetadataChange('displayOrder', parseInt(e.target.value))}
                className="af-settings-tab__input"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Primary Usage
              <input
                type="text"
                value={metadata.primaryUsage || ''}
                onChange={(e) => handleMetadataChange('primaryUsage', e.target.value)}
                className="af-settings-tab__input"
                placeholder="e.g., workflow, portal"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Node Usage
              <input
                type="text"
                value={metadata.nodeUsage || ''}
                onChange={(e) => handleMetadataChange('nodeUsage', e.target.value)}
                className="af-settings-tab__input"
                placeholder="e.g., approval, data collection"
              />
            </label>
          </div>
        </div>

        {/* Scripts & Styles */}
        <div className="af-settings-tab__section">
          <h3 className="af-settings-tab__section-title">Scripts &amp; Styles</h3>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Inline CSS
              <textarea
                value={metadata.formStyleBlock || ''}
                onChange={(e) => handleMetadataChange('formStyleBlock', e.target.value)}
                className="af-settings-tab__textarea"
                placeholder="/* CSS applied directly to the form */"
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Inline JavaScript
              <textarea
                value={metadata.formJsBlock || ''}
                onChange={(e) => handleMetadataChange('formJsBlock', e.target.value)}
                className="af-settings-tab__textarea"
                placeholder="// JavaScript executed when the form loads"
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              External CSS URLs
              <textarea
                value={(metadata.formCssFileUrls || []).join('\n')}
                onChange={(e) => handleMetadataChange('formCssFileUrls', e.target.value.split('\n').filter((u) => u.trim()))}
                className="af-settings-tab__textarea"
                placeholder={'https://example.com/styles.css\nhttps://example.com/theme.css'}
                rows={3}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <span className="af-settings-tab__hint">One URL per line</span>
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              External JS URLs
              <textarea
                value={(metadata.formJsFileUrls || []).join('\n')}
                onChange={(e) => handleMetadataChange('formJsFileUrls', e.target.value.split('\n').filter((u) => u.trim()))}
                className="af-settings-tab__textarea"
                placeholder={'https://example.com/lib.js\nhttps://example.com/init.js'}
                rows={3}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <span className="af-settings-tab__hint">One URL per line</span>
            </label>
          </div>
        </div>

        {/* Advanced */}
        <div className="af-settings-tab__section">
          <h3 className="af-settings-tab__section-title">Advanced</h3>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Sample Data (JSON)
              <textarea
                value={metadata.sampleData ? JSON.stringify(metadata.sampleData, null, 2) : ''}
                onChange={(e) => {
                  try {
                    const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : undefined;
                    handleMetadataChange('sampleData', parsed);
                  } catch {
                    // allow invalid JSON while typing — only commit valid
                  }
                }}
                className="af-settings-tab__textarea"
                placeholder="{}"
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <span className="af-settings-tab__hint">Used for form preview and testing</span>
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Custom Metadata (JSON)
              <textarea
                value={metadata.metadata ? JSON.stringify(metadata.metadata, null, 2) : ''}
                onChange={(e) => {
                  try {
                    const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : undefined;
                    handleMetadataChange('metadata', parsed);
                  } catch {
                    // allow invalid JSON while typing
                  }
                }}
                className="af-settings-tab__textarea"
                placeholder="{}"
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              HTML Card ID
              <input
                type="number"
                value={metadata.htmlCardId || ''}
                onChange={(e) => handleMetadataChange('htmlCardId', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-settings-tab__input"
                placeholder="Leave blank for none"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Content Decoration ID
              <input
                type="number"
                value={metadata.contentDecorationId || ''}
                onChange={(e) => handleMetadataChange('contentDecorationId', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-settings-tab__input"
                placeholder="Leave blank for none"
              />
            </label>
          </div>

          <div className="af-settings-tab__group">
            <label className="af-settings-tab__label">
              Styling Decoration ID
              <input
                type="number"
                value={metadata.stylingDecorationId || ''}
                onChange={(e) => handleMetadataChange('stylingDecorationId', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-settings-tab__input"
                placeholder="Leave blank for none"
              />
            </label>
          </div>
        </div>

        {/* Sections */}
        <div className="af-settings-tab__section">
          <div className="af-settings-tab__section-header">
            <h3 className="af-settings-tab__section-title">Form Sections</h3>
            <button
              onClick={handleAddSection}
              className="af-settings-tab__add-section-btn"
            >
              + Add Section
            </button>
          </div>

          {sections.length === 0 ? (
            <p className="af-settings-tab__empty-text">No sections defined. Add sections to organize form controls.</p>
          ) : (
            <div className="af-settings-tab__sections-list">
              {sections.map((section) => (
                <div key={section.id} className="af-settings-tab__section-item">
                  <div className="af-settings-tab__section-controls">
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => handleUpdateSection(section.id, { title: e.target.value })}
                      className="af-settings-tab__input"
                      placeholder="Section title"
                    />
                    <button
                      onClick={() => handleDeleteSection(section.id)}
                      className="af-settings-tab__delete-section-btn"
                    >
                      Delete
                    </button>
                  </div>

                  <textarea
                    value={section.description || ''}
                    onChange={(e) => handleUpdateSection(section.id, { description: e.target.value })}
                    className="af-settings-tab__textarea"
                    placeholder="Section description"
                    rows={2}
                  />

                  <div className="af-settings-tab__section-checkboxes">
                    <label className="af-settings-tab__checkbox-label">
                      <input
                        type="checkbox"
                        checked={section.collapsible || false}
                        onChange={(e) => handleUpdateSection(section.id, { collapsible: e.target.checked })}
                      />
                      Collapsible
                    </label>
                    <label className="af-settings-tab__checkbox-label">
                      <input
                        type="checkbox"
                        checked={section.defaultExpanded !== false}
                        onChange={(e) => handleUpdateSection(section.id, { defaultExpanded: e.target.checked })}
                      />
                      Default Expanded
                    </label>
                  </div>

                  <label className="af-settings-tab__label">
                    Columns
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={section.columns ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleUpdateSection(section.id, { columns: v === '' ? undefined : Math.max(1, Number(v)) });
                      }}
                      className="af-settings-tab__input"
                      placeholder="Uses the form's own column count"
                    />
                  </label>
                  <span className="af-settings-tab__hint">
                    Lays this section's controls out in a CSS grid with this many columns
                    (e.g. 3 for filter fields side-by-side instead of one per row). Leave
                    blank to use the form's overall "Number of Columns" setting.
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SettingsTab.displayName = 'SettingsTab';
